<script setup lang="ts">
import { storeToRefs } from "pinia";
import type { ComputedRef, Ref } from "vue";
import { onMounted } from "vue";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
  DISTINCT_VALUES_KEY,
} from "~/core/constants/constants";
import { useDeviceTypeFilters } from "~/modules/dtwin/composables/useDeviceTypeFilters";
import { useFetchDtwinModels } from "~/modules/dtwin/composables/useFetchDtwinModels";
import { useFetchDtwins } from "~/modules/dtwin/composables/useFetchDtwins";
import {
  DISTINCT_VALUES_LIFECYCLE,
  PATH_DTWINS_ID,
} from "~/modules/dtwin/constants";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { DtwinTypeFilterOptions } from "~/modules/dtwin/models/DtwinTypeOptions.interface";
import { getDtwinTypeLabel } from "~/modules/dtwin/utils/getDtwinTypeLabel";
import { getLifeCycleStateProperties } from "~/modules/dtwin/utils/getLifeCycleStateProperties";
import { getLifeCycleStatesOptions } from "~/modules/dtwin/utils/getLifeCycleStatesOptions";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import { router } from "~/plugins/router";
import { type FilterOptions, Filters, SectorResource } from "~/types";

const props = defineProps<{
  sector: Sector;
}>();

const { t, te } = useI18n();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const {
  isLoading: isLoadingDtwins,
  results: dtwins,
  error: errorDtwins,
  fetchData,
  getDescription,
  canManageItems,
  setFilter,
  search,
  filters,
  onSearch,
} = useFetchDtwins({
  organizationCode: currentOrganization.value?.code,
  sectorCode: props.sector.code,
});

const {
  isLoading: isLoadingAllDtwinModels,
  error: errorAllDtwinModels,
  results: resultsAllDtwinModels,
  fetchAll: fetchAllDtwinModels,
} = useFetchDtwinModels({
  organizationCode: currentOrganization.value?.code,
});

const selectedItems: Ref<Dtwin[]> = ref([]);
const displayActions = computed(() => canManageItems(props.sector));
const deviceTypeOptions: Ref<DtwinTypeFilterOptions | undefined> = ref();

const isLoading = computed(
  () => isLoadingAllDtwinModels.value || isLoadingDtwins.value,
);

const searchQuery = computed({
  get() {
    return (filters.get(Filters.DTWIN_NAME_OR_SERIAL_NUMBER) ?? "") as string;
  },
  set(searchQuery) {
    setFilter(Filters.DTWIN_NAME_OR_SERIAL_NUMBER, searchQuery);
  },
});

const lifecycleStatesOptions: ComputedRef<FilterOptions> = computed(() =>
  getLifeCycleStatesOptions(t, te, dtwins.value?.distinctValues),
);

async function refresh() {
  await reloadData();
  selectedItems.value = [];
}

async function reloadData(page?: number, pageSize?: number) {
  const params = new Map();
  params.set(DISTINCT_VALUES_KEY, DISTINCT_VALUES_LIFECYCLE);
  await fetchData(page, pageSize, params);
}

function onRowClick(dtwin: Dtwin) {
  router.push({ name: PATH_DTWINS_ID, params: { id: dtwin.uid } });
}

async function getNextByPageAndSize(
  page: number = DEFAULT_FIRST_PAGE,
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  await reloadData(page, pageSize);
}

onMounted(async () => {
  await fetchAllDtwinModels();
  const { deviceTypeFilters } = await useDeviceTypeFilters(
    currentOrganization.value?.code,
  );
  deviceTypeOptions.value = {
    label: t("dtwins.filters.deviceType"),
    options: deviceTypeFilters.value.map((model) => ({
      value: model.uids || [],
      label: model.label,
      name: model.value,
    })),
  };
});

onSearch(refresh);

defineExpose({
  refresh,
});
</script>

<template>
  <container-component
    :px="0"
    :py="0"
    :is-loading="isLoading && !dtwins"
    :error="errorAllDtwinModels || errorDtwins"
  >
    <div class="mb-4 flex gap-8 items-start">
      <search-bar
        v-model:search-query="searchQuery"
        class="flex-1 min-w-[400px]"
        :search-placeholder="t('dtwins.filters.searchByNameOrSerialNumber')"
        @clear="search"
        @search="search"
      />
      <dtwin-filters
        class="!mb-0"
        :organization-code="currentOrganization?.code"
        :filters="filters"
        :type-options="deviceTypeOptions"
        :lifecycle-states-options="lifecycleStatesOptions"
        @change="setFilter"
        @enter="search"
      />
    </div>

    <sectors-details-table
      v-model="selectedItems"
      primary-key="uid"
      :is-loading="isLoading"
      :selection="displayActions && !sector.isRootSector"
      :min-width="displayActions && !sector.isRootSector ? '47rem' : '44rem'"
      :items="dtwins"
      :display-actions="displayActions"
      :resource="SectorResource.DTWINS"
      :get-description="getDescription"
      :sector="sector"
      selection-message="dtwins.fleet.selectedDevices"
      @pagination:get-next-page="getNextByPageAndSize"
      @row-click="onRowClick"
      @actions:detach="refresh"
    >
      <lxc-table-column
        prop="attributes.friendlyName"
        :label="t('dtwins.list.attributes.friendlyName')"
        min-width="10rem"
      />
      <lxc-table-column
        prop="attributes.type"
        :label="t('dtwins.list.attributes.type')"
        width="10rem"
      >
        <template v-if="resultsAllDtwinModels" #default="scope">
          {{ getDtwinTypeLabel(resultsAllDtwinModels, scope.row) }}
        </template>
      </lxc-table-column>
      <lxc-table-column
        prop="attributes.serialNumber"
        :label="t('dtwins.list.attributes.serialNumber')"
        width="12rem"
      />
      <lxc-table-column
        :label="t('dtwins.list.attributes.state')"
        width="12rem"
      >
        <template #default="scope">
          {{
            getLifeCycleStateProperties(scope.row.lifeCycleState, t, te).label
          }}
        </template>
      </lxc-table-column>
    </sectors-details-table>
  </container-component>
</template>
